import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import SvgNotFound from '@components/icons/NotFound';

type Format = 'auto' | 'webp' | 'avif' | 'orig';

interface WrapperImageProps {
  src: string;
  alt?: string;

  responsive?: boolean; // default true
  size?: number; // pokud responsive=false
  className?: string;
  bgClassName?: string;

  useImgproxy?: boolean;
  imgproxyBase?: string;
  preventEnlarge?: boolean;
  format?: Format;
  quality?: number;

  /** měřítko obrázku vůči boxu (0–1), default 1.0 (100%) */
  innerScale?: number;
}

const WrapperImage: React.FC<WrapperImageProps> = ({
  src,
  alt = '',
  responsive = true,
  size = 80,
  className,
  bgClassName = 'bg-white',
  useImgproxy = true,
  imgproxyBase = 'https://portal-dev1.pecosta.dev/services/imgproxy',
  preventEnlarge = false,
  format = 'auto',
  quality = 85,
  innerScale = 0.85,
}) => {
  const hasSrc = typeof src === 'string' && src.trim().length > 0;
  const [imgError, setImgError] = useState(false);

  const imgUrl = useMemo(() => {
    if (!hasSrc || !useImgproxy) return src || '';

    const target = responsive ? 512 : size;
    const enlarge = preventEnlarge ? 0 : 1;

    const fmtSuffix = format === 'webp' ? '@webp' : format === 'avif' ? '@avif' : format === 'orig' ? '' : '@webp';

    const qPart = format === 'orig' ? '' : `/quality:${Math.max(1, Math.min(100, quality))}`;
    const encoded = encodeURI(src);

    return `${imgproxyBase}/insecure/resize:fit:${target}:${target}:${enlarge}/gravity:ce${qPart}/plain/${encoded}${fmtSuffix}`;
  }, [hasSrc, useImgproxy, src, responsive, size, preventEnlarge, format, quality, imgproxyBase]);

  const wrapperClasses = clsx('relative overflow-hidden flex items-center justify-center', responsive ? 'w-full aspect-square' : undefined, bgClassName, className);

  const wrapperStyle = responsive ? undefined : { width: size, height: size };

  if (!hasSrc || imgError) {
    return (
      <div className={wrapperClasses} style={wrapperStyle}>
        <SvgNotFound className="w-1/2 h-1/2 text-gray-300" />
      </div>
    );
  }

  return (
    <div className={wrapperClasses} style={wrapperStyle}>
      <img
        src={imgUrl}
        alt={alt}
        className="block object-contain"
        style={{
          maxWidth: `${innerScale * 100}%`,
          maxHeight: `${innerScale * 100}%`,
        }}
        loading="lazy"
        decoding="async"
        onError={() => setImgError(true)}
      />
    </div>
  );
};

export default WrapperImage;
